'use client';

import { JSFunction } from '@ahm/common-helpers';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/lib/is-mobile';

export const DetectWebinappContext = createContext(false);

export function useDetectWebinappContext() {
  return useContext(DetectWebinappContext);
}

export const useDetectWebInApp = () => {
  const isMobile = useIsMobile();
  const contextWebinapp = useDetectWebinappContext();

  // Immediate detection based on URL params and localStorage for stable initial render
  const initialWebInApp = useMemo(() => {
    if (typeof window === 'undefined') return contextWebinapp;

    const urlParams = new URLSearchParams(window.location.search);
    const hasWebInAppParam = urlParams.has('webinapp');
    const storedWebInApp = localStorage.getItem('isWebinapp') === 'true';

    return isMobile && (hasWebInAppParam || storedWebInApp);
  }, [isMobile, contextWebinapp]);

  const [isWebinapp, setIsWebinapp] = useState(initialWebInApp);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Sử dụng localStorage thay vì sessionStorage để tránh mất data khi app chạy ngầm lâu
    const storedWebInApp = localStorage.getItem('isWebinapp') === 'true';
    const urlParams = new URLSearchParams(window.location.search);
    const hasWebInAppParam = urlParams.has('webinapp');

    if ((hasWebInAppParam || storedWebInApp) && isMobile) {
      if (!storedWebInApp) {
        localStorage.setItem('isWebinapp', 'true');
        console.log('🔧 WebInApp detected and saved to localStorage');
      }

      // Only update state if different to prevent unnecessary re-renders
      if (!isWebinapp) {
        setIsWebinapp(true);
      }

      JSFunction.call({ name: 'hide_toolbar' });

      // Xóa webinapp parameter sau khi đã xử lý để cleanup URL
      if (hasWebInAppParam) {
        setTimeout(() => {
          try {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete('webinapp');
            window.history.replaceState({}, '', currentUrl.toString());
            console.log('🔧 Removed webinapp parameter from URL');
          } catch (error) {
            console.error('Error removing webinapp parameter:', error);
          }
        }, 500); // Reduced delay from 1000ms to 500ms
      }
    } else if (!hasWebInAppParam && !storedWebInApp && isWebinapp) {
      // Reset WebInApp state if conditions no longer met
      setIsWebinapp(false);
    }
  }, [isMobile, isWebinapp]);

  return useMemo(() => isWebinapp, [isWebinapp]);
};
